import { AsyncSelect } from "@neonumy/ant-design";
import { clientCentralApiStrawberry, clientNeoFact } from "@neonumy/api-clients";
import { Col, Form, Input, Row, Select, Space } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { CreatableAsyncPaginate } from "components/AsyncSelect/CreatableAsyncSelect";
import CardTitle from "components/CardTitle";
import CompaniesAutocomplete from "components/CompaniesAutocomplete";
import {
  GET_LOCATIONS_LIST,
  LIST_COUNTRIES_QUERY,
  LIST_LEGAL_STRUCTURES,
} from "queries/centralApi/centralApi";
import { LIST_CLIENT_VAT_TYPES_FOR_ASYNC_SELECT } from "queries/centralApi/clients";
import { useTranslation } from "react-i18next";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaBuilding } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import useGetClientTypeOptions from "views/Clients/hooks/clients/useGetClientTypeOptions";
import { createOptionLegalStructures } from "utils/selectOptions";
import { generateIntracomVatNumber } from "utils/vatUtils";
import useCoreStore from "views/hooks/useCoreStore";

interface CreateClientFormPropsType {
  form: FormInstance<Record<string, any>>;
  onValuesChange: (changedValues: Record<string, any>, allValues: Record<string, any>) => void;
  onFinish: (values: Record<string, any>) => Promise<void>;
}

export default function CreateClientForm(props: CreateClientFormPropsType): React.ReactElement {
  const { form, onValuesChange, onFinish } = props;
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const { options: clientTypeOptions } = useGetClientTypeOptions();

  const currency = useCoreStore(state => state.currency);
  const companySettings = useCoreStore(state => state.companySettings);
  const clientType = Form.useWatch("clientType", form);
  const country = Form.useWatch("country", form)?.value;

  const currencyCode = currency?.code;
  const isPublicAdminClientType = clientType === "pu";
  const isParticularClientType = clientType === "pa";
  const clientHasAutoNumber = companySettings?.clientHasAutoNumber;
  const isSiretIntracomRequired = !isParticularClientType && country === "FR";

  const handleValuesChange = (
    changedValues: Record<string, any>,
    allValues: Record<string, any>,
  ) => {
    if (changedValues.vatNumber && !allValues.euVatNumber) {
      const vatNumber = changedValues.vatNumber;
      const cleanVatNumber = typeof vatNumber === "string" ? vatNumber.split(" ")[0] : vatNumber;
      const intracomVatNumber = generateIntracomVatNumber(cleanVatNumber);

      if (intracomVatNumber) {
        form.setFieldsValue({
          euVatNumber: intracomVatNumber,
        });
      }
    }

    onValuesChange(changedValues, allValues);
  };

  return (
    <Form
      name="createClient"
      layout="vertical"
      form={form}
      onFinish={onFinish}
      initialValues={{ currency: currencyCode }}
      autoComplete="off"
      onValuesChange={handleValuesChange}
    >
      <Space style={{ marginBottom: 8 }}>
        <CardTitle icon={<FaBuilding />} title={t("General information")} />
      </Space>
      <Row gutter={[8, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item
            name="clientType"
            label={t("Client type")}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <Select options={clientTypeOptions} />
          </Form.Item>
        </Col>
        {!clientHasAutoNumber && (
          <Col xs={24} sm={6}>
            <Form.Item
              rules={[{ required: !clientHasAutoNumber, message: t("This field is required") }]}
              name="code"
              label={t("Client number")}
            >
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col xs={24} sm={16}>
          <Form.Item
            name="name"
            label={t("Company name or Client name")}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <CompaniesAutocomplete
              variablesExtractor={(searchTerm: string) => ({
                limit: 10,
                nameContains: searchTerm,
                orderBy: ["name"],
              })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="vatNumber"
            label={t("Siret")}
            rules={[{ required: isSiretIntracomRequired, message: t("This field is required") }]}
          >
            <CompaniesAutocomplete
              isVat={true}
              variablesExtractor={(searchTerm: string) => ({
                limit: 10,
                vatNumberStartswith: searchTerm,
                orderBy: ["name"],
              })}
              disabled={isParticularClientType}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="euVatNumber"
            label={t("VAT Intracom")}
            rules={[{ required: isSiretIntracomRequired, message: t("This field is required") }]}
          >
            <Input maxLength={13} />
          </Form.Item>
        </Col>
        {isPublicAdminClientType && (
          <Col xs={24} sm={8}>
            <Form.Item
              name="chorus"
              label={t("Chorus")}
              rules={[
                {
                  pattern: new RegExp("^[0-9]*$"),
                  message: t("This field only accept numbers"),
                },
              ]}
            >
              <Input maxLength={21} />
            </Form.Item>
          </Col>
        )}
        {!isPublicAdminClientType && (
          <Col xs={24} sm={8}>
            <Form.Item name="legalStructure" label={t("Legal form")}>
              <AsyncSelect
                optionsFormat={createOptionLegalStructures}
                hasQueryFilter={true}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listLegalStructures",
                  query: LIST_LEGAL_STRUCTURES,
                  variables: {
                    filter: {
                      search: "",
                    },
                  },
                }}
                translations={{
                  placeholder: t(""),
                  loadingMessage: t("Loading"),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
        )}
        <Col xs={24} sm={8}>
          <Form.Item
            rules={[{ required: true, message: t("This field is required") }]}
            name="clientVatTypeId"
            label={t("Client vat type")}
          >
            <AsyncSelect
              hasQueryFilter={true}
              queryOptions={{
                client: clientNeoFact,
                dataName: "listClientVatTypes",
                query: LIST_CLIENT_VAT_TYPES_FOR_ASYNC_SELECT,
                variables: {
                  filter: {
                    search: "",
                    companyId: urlParams.companyId,
                  },
                  limit: 100,
                },
              }}
              optionsFormat={item => ({
                label: `${item.code} - ${item.name}`,
                value: item.id,
              })}
              translations={{
                placeholder: t(""),
                loadingMessage: t("Loading"),
                noOptionsMessage: t("No Options"),
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Space style={{ marginBottom: 8 }}>
          <CardTitle icon={<FaMapMarkerAlt />} title={t("Address")} />
        </Space>
      </Row>
      <Row gutter={[8, 0]}>
        <Col xs={24} sm={16}>
          <Form.Item name="name" label={t("Name")}>
            <Input autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={4}>
          <Form.Item name="addressNr" label={t("Number")}>
            <Input autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={4}>
          <Form.Item name="addressBtq" label={t("BTQ")}>
            <Input
              maxLength={1}
              onChange={e => {
                const value = e.target.value.toUpperCase();
                if (value && !["BT", "Q", "B", "T"].includes(value)) {
                  form.setFieldsValue({ addressBtq: "" });
                } else {
                  form.setFieldsValue({ addressBtq: value });
                }
              }}
              autoComplete="new-password"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24}>
          <Form.Item
            name="streetName"
            label={t("Address")}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <Input maxLength={250} autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24}>
          <Form.Item name="streetNameComplement" label={t("Additional address")}>
            <Input maxLength={250} autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label={t("Country")}
            name="country"
            initialValue={{ label: t("France"), value: "FR" }}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <AsyncSelect
              optionsFormat={item => ({
                label: item.name,
                value: item.code,
                data: item,
              })}
              onChange={() => {
                form.setFieldsValue({
                  city: undefined,
                  postalCode: undefined,
                });
              }}
              queryOptions={{
                client: clientCentralApiStrawberry,
                dataName: "listCountriesPaginated",
                query: LIST_COUNTRIES_QUERY,
                variables: {
                  search: "",
                  limit: 100,
                },
              }}
              translations={{
                placeholder: t(""),
                loadingMessage: t("Loading"),
                noOptionsMessage: t("No Options"),
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="postalCode"
            label={t("Postal code")}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <CreatableAsyncPaginate
              isDisabled={!country}
              getOptionValue={(option: Record<string, any>) => option.id || option.value}
              queryOptions={{
                client: clientCentralApiStrawberry,
                dataName: "listLocations",
                query: GET_LOCATIONS_LIST,
                variables: {
                  limit: 20,
                  orderBy: ["postal_code"],
                  countryCode: country,
                },
              }}
              optionsFormat={(item: Record<string, unknown>) => {
                const it = item as { postalCode: string; city: string };
                return {
                  label: `${it.postalCode} - ${it.city}`,
                  value: `${it.postalCode}`,
                  id: `${it.postalCode}-${it.city}`,
                  data: item,
                };
              }}
              onCreateOption={(value: string) => {
                form.setFieldsValue({
                  ...form.getFieldsValue(true),
                  postalCode: { label: value, value },
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="city"
            label={t("City")}
            rules={[{ required: true, message: t("This field is required") }]}
          >
            <CreatableAsyncPaginate
              isDisabled={!country}
              getOptionValue={(option: Record<string, any>) => option.id || option.value}
              queryOptions={{
                client: clientCentralApiStrawberry,
                dataName: "listLocations",
                query: GET_LOCATIONS_LIST,
                variables: {
                  limit: 20,
                  orderBy: ["city"],
                  countryCode: country,
                },
              }}
              optionsFormat={(item: Record<string, unknown>) => {
                const it = item as { city: string; postalCode: string };
                return {
                  label: it.city,
                  value: it.city,
                  id: `${it.city}-${it.postalCode}`,
                  data: item,
                };
              }}
              onCreateOption={(value: string) => {
                form.setFieldsValue({
                  ...form.getFieldsValue(true),
                  city: { label: value, value },
                });
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
